"use client";

import { useState } from "react";
import type { MortgageCalcInput, MortgageCalcResponse } from "@/types/mortgage";

export default function PricerPage() {
  const [formData, setFormData] = useState<MortgageCalcInput>({
    loanOfficer: "Jane Doe",
    borrowerLastName: "",
    borrowerFirstName: "",
    leadSource: "Web",
    veteranFlag: false,
    numberOfBorrowers: 1,
    homeValue: 850000,
    state: "CA",
    age: 72,
    expectedInitialLoanBalance: 120000,
    ficoScore: 735,
  });

  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<MortgageCalcResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const res = await fetch("/api/mortgage/calculate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to calculate");
      }

      const data: MortgageCalcResponse = await res.json();
      setResponse(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : type === "number"
          ? Number(value)
          : value,
    }));
  };

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="mb-4 text-2xl font-bold">Pricer</h1>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-lg bg-white p-4 shadow">
          <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-4">
            <div>
              <label className="mb-0.5 block text-xs font-medium">
                Loan Officer <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="loanOfficer"
                value={formData.loanOfficer}
                onChange={handleChange}
                required
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="mb-0.5 block text-xs font-medium">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="borrowerFirstName"
                value={formData.borrowerFirstName}
                onChange={handleChange}
                required
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="mb-0.5 block text-xs font-medium">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="borrowerLastName"
                value={formData.borrowerLastName}
                onChange={handleChange}
                required
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="mb-0.5 block text-xs font-medium">
                Lead Source <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="leadSource"
                value={formData.leadSource}
                onChange={handleChange}
                required
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="mb-0.5 block text-xs font-medium">
                Home Value <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="homeValue"
                value={formData.homeValue}
                onChange={handleChange}
                required
                min="0"
                step="1000"
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="mb-0.5 block text-xs font-medium">
                State <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
                maxLength={2}
                placeholder="CA"
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm uppercase focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="mb-0.5 block text-xs font-medium">
                Age <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="age"
                value={formData.age}
                onChange={handleChange}
                required
                min="0"
                max="120"
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="mb-0.5 block text-xs font-medium">
                Initial Balance <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="expectedInitialLoanBalance"
                value={formData.expectedInitialLoanBalance}
                onChange={handleChange}
                required
                min="0"
                step="1000"
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="mb-0.5 block text-xs font-medium">
                FICO Score <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="ficoScore"
                value={formData.ficoScore}
                onChange={handleChange}
                required
                min="300"
                max="850"
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="mb-0.5 block text-xs font-medium">
                # Borrowers <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="numberOfBorrowers"
                value={formData.numberOfBorrowers}
                onChange={handleChange}
                required
                min="1"
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-end pb-1.5">
              <label className="flex items-center text-xs font-medium">
                <input
                  type="checkbox"
                  name="veteranFlag"
                  checked={formData.veteranFlag}
                  onChange={handleChange}
                  className="mr-1.5 h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Veteran
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-4 w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400"
          >
            {loading ? "Calculating..." : "Calculate"}
          </button>
        </div>
      </form>

      {/* Error Message */}
      {error && (
        <div className="mt-6 rounded-lg bg-red-50 p-4 text-red-800">
          <p className="font-medium">Error:</p>
          <p>{error}</p>
        </div>
      )}

      {/* Response */}
      {response && (
        <div className="mt-6 rounded-lg bg-white p-6 shadow">
          <h2 className="mb-4 text-xl font-semibold">Results</h2>
          <div className="mb-4 text-sm text-gray-600">
            <p>
              <strong>Request ID:</strong> {response.requestId}
            </p>
            <p>
              <strong>Created:</strong>{" "}
              {new Date(response.createdAt).toLocaleString()}
            </p>
          </div>

          <div className="space-y-4">
            {response.products.map((product, index) => (
              <div key={index} className="rounded border border-gray-200 p-4">
                <h3 className="mb-3 text-lg font-semibold">
                  {product.productType}
                </h3>
                <div className="grid gap-2 text-sm md:grid-cols-2">
                  <div>
                    <span className="font-medium">Expected Rate:</span>{" "}
                    {(product.expectedRate * 100).toFixed(2)}%
                  </div>
                  <div>
                    <span className="font-medium">PLF Rate:</span>{" "}
                    {(product.plfRate * 100).toFixed(2)}%
                  </div>
                  <div>
                    <span className="font-medium">Principal Limit:</span> $
                    {product.principalLimit.toLocaleString()}
                  </div>
                  <div>
                    <span className="font-medium">Origination Fee:</span> $
                    {product.originationFee.toLocaleString()}
                  </div>
                  <div>
                    <span className="font-medium">
                      Discounted Origination Fee:
                    </span>{" "}
                    ${product.discountedOriginationFee.toLocaleString()}
                  </div>
                  <div>
                    <span className="font-medium">Credit at Closing:</span> $
                    {product.creditAtClosing.toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
